'use client';

import cn from 'classnames';
import Script from 'next/script';
import React, { useEffect, useState } from 'react';
import Scrollbar from 'react-scrollbars-custom';
import { ContentBlock } from 'src/components/Article/ContentBlock';
import { Info } from 'src/components/Article/Info';
import { NavBar, NavBarProps } from 'src/components/Article/NavBar';
import { SocialButtonsShare } from 'src/components/Article/SocialButtonsShare';
import { SubscribeAd } from 'src/components/Article/SubscribeAd';
import { WarpsCarousel } from 'src/components/Carousel/Carousels';
import { Banner } from 'src/components/ConstructorBlocks/Banner';
import { Wrapper } from 'src/components/Wrapper';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useAsPath } from 'src/hooks/useAsPath';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { Layout } from 'src/layout';
import { ArticleData } from 'src/types';
import { BlogsProps } from 'src/types/backendContent';
import { Breadcrumbs } from 'src/uikit/Breadcrumbs';
import { Form } from 'src/uikit/Form';
import { formatDate } from 'src/utils/formatDate';
import { getTimeToRead } from 'src/utils/getTimeToRead';
import { handleScroll } from 'src/utils/handleScroll';

import s from '../../../components/Article/Article.module.scss';

function ArrowUp() {
  return (
    <svg
      width="12"
      height="8"
      viewBox="0 0 12 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0.353553 5.94152C0.158291 6.13679 0.158291 6.45337 0.353553 6.64863L1.05683 7.35191C1.25194 7.54702 1.56823 7.54719 1.76355 7.3523L5.64683 3.47748C5.84201 3.28273 6.15799 3.28273 6.35317 3.47748L10.2364 7.3523C10.4318 7.54719 10.7481 7.54702 10.9432 7.35191L11.6464 6.64863C11.8417 6.45337 11.8417 6.13679 11.6464 5.94152L6.35355 0.648632C6.15829 0.45337 5.84171 0.45337 5.64645 0.648632L0.353553 5.94152Z"
        fill="currentColor"
      />
    </svg>
  );
}
interface ArticleContentProps {
  data: ArticleData;
  setModalShow: React.Dispatch<React.SetStateAction<boolean>>;
}

function Content({ data, setModalShow }: ArticleContentProps) {
  const asPath = useAsPath();
  const { clickAnalytics } = useAnalytics();

  let share = asPath.toString();
  if (!share.includes('http') || !share.includes('https'))
    share = `https://cloud.ru${asPath.toString()}`;

  const createdAt = formatDate(data.releaseDate);

  const timeToRead = data.timeToRead
    ? data.timeToRead
    : getTimeToRead(data.content);

  return (
    <div className={s.content}>
      <Info
        authors={data.authors}
        title={data.title}
        description={data.description}
        articleCreatedAt={createdAt}
        articleTimeToRead={timeToRead}
        categoryTitle={data.categoryTitle}
      />
      <ContentBlock content={data.content} />
      <Banner
        data-attribute="no_search_index"
        title="Запросите бесплатную консультацию по вашему проекту"
        buttonTitle="Связаться с нами"
        image="https://cdn.cloud.ru/backend/warp/cloud-banner.png"
        buttonOnClickAction={() => setModalShow(true)}
        buttonID="warp-banner-modal"
      />
      <SocialButtonsShare linkForSharing={share} />
      <div
        className={s.icon}
        onClick={() => {
          handleScroll();
          clickAnalytics({
            action: 'click',
            clickZone: 'body',
            clickElement: 'buttonclick',
            clickContent: 'scroll-top-button',
            uniqueId: `scroll-top-button`,
            transitionType: 'inside-link',
          });
        }}
      >
        <ArrowUp />
      </div>
    </div>
  );
}

type BlogPageType = {
  blog?: ArticleData;
  carousel?: BlogsProps[];
  menuItems?: NavBarProps[];
};

export function BlogPage({ blog, carousel, menuItems }: BlogPageType) {
  const asPath = useAsPath();
  const [adSubscribeWarp, setAdSubscribeWarp] = useState(false);
  const [modalShow, setModalShow] = useState(false);

  const [userSubscribed, setUserSubscribed] = useLocalStorage(
    'userSubscribed',
    'false',
  );

  useEffect(() => {
    if (userSubscribed === 'false') setAdSubscribeWarp(true);
  }, [userSubscribed]);

  const breadcrumbs = [
    {
      title: 'Главная',
      link: `/`,
    },
    {
      title: 'Блог',
      link: `/blog`,
    },
    {
      title: blog?.title,
    },
  ];

  const jsonBlog = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://cloud.ru/${asPath}`,
    },
    headline: blog?.title,
    image: '',
    author: {
      '@type': 'Person',
      name: blog?.authors
        ? `${blog.authors[0].name} ${blog.authors[0].secondName}`
        : '',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Cloud.ru',
      logo: {
        '@type': 'ImageObject',
        url: 'https://cloud.ru/favicons/logo-180.png',
      },
    },
    datePublished: blog?.releaseDate,
    cover: blog?.cover,
  };

  return (
    <Layout>
      <div className={cn(s.root)}>
        <Wrapper>
          <Breadcrumbs links={breadcrumbs} className={s.breadcrumbs} />
          <div className={s.main}>
            {blog ? <Content setModalShow={setModalShow} data={blog} /> : null}
            <Scrollbar className={s.right}>
              {menuItems && menuItems.length > 2 && <NavBar menu={menuItems} />}
              {adSubscribeWarp && (
                <SubscribeAd onClose={() => setAdSubscribeWarp(false)} />
              )}
            </Scrollbar>
          </div>
        </Wrapper>

        {carousel && carousel.length > 0 && (
          <WarpsCarousel
            title="Вам может понравиться"
            slides={carousel}
            datePosition="top"
          />
        )}
      </div>
      {modalShow && (
        <Form
          title="Связаться с нашим специалистом"
          inputPath="v2/order"
          additionalFields={{ subsegment: 'consultation' }}
          allFormsSentFormType="consultation"
          allFormsSentIsPopup
          modalClose={() => setModalShow(false)}
          analyticsFieldFill={{
            formType: 'popup',
            popupPlace: 'body',
            buttonId: 'blog-banner-modal',
          }}
          formVariant="modal"
          successfulSubmissonFormAnalytics={{
            formname: 'consultation',
          }}
        />
      )}

      <Script
        id="microblog"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonBlog) }}
      />
    </Layout>
  );
}
